import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import dict from "../../../Data/dict.json";
import FLOAT_MENU from "../../common_trivial/FLOAT_MENU";

const MAX_HEIGHT = "300px";

const Icon = () => (
  <svg
    style={{ width: "16px", height: "16px" }}
    aria-hidden="true"
    fill="currentColor"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
  </svg>
);

const SELECT_PHONE = ({
  value: propValue,
  options = [],
  onChange,
  className = "",
  filter_on = true,
  placeholder,
  ...props
}) => {
  const language = localStorage?.language ? localStorage?.language : "ina";
  const defaultPlaceholder =
    placeholder || dict["Select"]?.[language] || "Select";

  const mode_map = useSelector((state) => state.properties?.mode_map);
  const isViewer = mode_map === "viewer";

  const inputRef = useRef(null);
  const searchRef = useRef(null);
  const floatMenuRef = useRef(null);

  const [filter_text, setFilterText] = useState("");
  const [is_active, setIsActive] = useState(false);
  const [value, setValue] = useState(propValue || "62");
  const [anchor, setAnchor] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  useEffect(() => {
    if (propValue !== value) {
      setValue(propValue);
    }
  }, [propValue, value]);

  // FIX 1: Slightly delay focus to ensure portal is mounted before stealing focus
  useEffect(() => {
    if (is_active) {
      setTimeout(() => {
        if (searchRef?.current) searchRef.current.focus();
      }, 50);
      setFilterText("");
      setFocusedIndex(-1);
    }
  }, [is_active]);

  useEffect(() => {
    const handler = (e) => {
      if (
        (inputRef.current && inputRef.current.contains(e.target)) ||
        (floatMenuRef.current && floatMenuRef.current.contains(e.target))
      ) {
        return;
      }
      setIsActive(false);
    };

    window.addEventListener("mousedown", handler); // Changed to mousedown for faster trap
    return () => window.removeEventListener("mousedown", handler);
  }, []);

  const filteredOptions = options.filter((opt) => {
    if (!filter_text) return true;
    const searchLower = filter_text.toLowerCase();
    const nameMatch = String(opt.name || "")
      .toLowerCase()
      .includes(searchLower);
    const codeMatch = String(opt.phone_code || "").includes(searchLower);
    return nameMatch || codeMatch;
  });

  const filterOnChange = (e) => {
    setFilterText(e.target.value || "");
    setFocusedIndex(-1);
  };

  const toggleDropdown = (event) => {
    if (isViewer) return;
    setIsActive(!is_active);
    setAnchor(event?.currentTarget);
  };

  const handleClick = (codeValue) => {
    setValue(codeValue);
    if (onChange) onChange(codeValue);
    setIsActive(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!is_active || isViewer) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : prev,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
        e.preventDefault();
        handleClick(filteredOptions[focusedIndex].phone_code);
      }
    }
  };

  useEffect(() => {
    if (focusedIndex >= 0) {
      const el = document.getElementById(`phone-option-${focusedIndex}`);
      if (el) el.scrollIntoView({ block: "nearest" });
    }
  }, [focusedIndex]);

  const getDisplay = () => {
    if (!value) {
      return (
        <div
          className="badge_small background_grey_light"
          style={{ margin: 0 }}
        >
          {defaultPlaceholder}
        </div>
      );
    }
    return (
      <div className="badge_small background_black" style={{ margin: 0 }}>
        +{value}
      </div>
    );
  };

  return (
    <main style={{ width: "100%", height: "100%" }}>
      <section
        {...props}
        className={`noselect ${!isViewer ? "cursor_pointer" : ""} ${className}`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          height: "100%",
          border: "none",
          outline: "none",
          boxSizing: "border-box",
          cursor: isViewer ? "default" : "pointer",
        }}
        onClick={toggleDropdown}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleDropdown(event);
          }
        }}
        ref={inputRef}
        tabIndex={0}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            minWidth: 0,
          }}
        >
          {getDisplay()}
        </div>
        <div
          style={{
            marginLeft: "8px",
            flexShrink: 0,
            color: "#666",
            opacity: isViewer ? 0.5 : 1,
          }}
        >
          <Icon />
        </div>
      </section>

      {is_active && (
        <FLOAT_MENU anchorEl={anchor} open={is_active} onClose={toggleDropdown}>
          <div
            ref={floatMenuRef}
            tabIndex={-1}
            onKeyDown={handleKeyDown}
            className="select-phone-portal-container" // FIX 2: Explicit Target Class
            style={{ outline: "none", padding: "8px", minWidth: "200px" }}
          >
            {filter_on && (
              <input
                className="margin_bottom"
                type="text"
                ref={searchRef}
                value={filter_text}
                onChange={filterOnChange}
                placeholder="Search country or code..."
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "4px 8px",
                }}
                onClick={(e) => e.stopPropagation()}
              />
            )}
            <div
              className="option_list_box_dropdown"
              style={{ maxHeight: MAX_HEIGHT, overflowY: "auto" }}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt, idx) => {
                  const isSelected = String(value) === String(opt.phone_code);
                  const isFocused = focusedIndex === idx;

                  return (
                    <div
                      key={`${opt.name}-${idx}`}
                      id={`phone-option-${idx}`}
                      onMouseDown={(e) => e.preventDefault()} // FIX 3: Prevents focus drop when clicking option
                      onClick={() => handleClick(opt.phone_code)}
                      className={`badge_small margin_bottom cursor_pointer ${
                        isSelected
                          ? "background_black"
                          : "background_grey_light"
                      }`}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "12px",
                        boxShadow: isFocused ? "0 0 0 2px black" : "none",
                        position: isFocused ? "relative" : "static",
                        zIndex: isFocused ? 1 : "auto",
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "150px",
                        }}
                        title={opt.name}
                      >
                        {opt.name}
                      </span>
                      <span style={{ fontWeight: "bold", flexShrink: 0 }}>
                        +{opt.phone_code}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div
                  style={{ textAlign: "center", color: "#999", padding: "8px" }}
                >
                  No results found
                </div>
              )}
            </div>
          </div>
        </FLOAT_MENU>
      )}
    </main>
  );
};

export default SELECT_PHONE;
